import autobind from 'core-decorators/lib/autobind';
import { connect } from 'react-redux';
import { Player as VODPlayer, OVERLAY_ACTION_TYPES } from 'widget/player-widget';
import NewPlayer from 'widget/player-widget/components/player/new-player';

import { VODBiEvents } from 'shared/utils/wix-connect';
import { getBuyButtonDisplayType } from 'widget/utils/overlay-buttons-utils';
import { canAccessFullVideo } from 'widget/ui-selectors/video-access';

import { isExternalVideo } from 'shared/ui-selectors/video/video-origin';
import getVideoUrls from 'widget/selectors/get-video-urls';
import { isNewPlayerEnabled } from 'shared/selectors/experiments';
import { getAllSettings } from 'shared/selectors/app-settings';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';

import fetchVideoUrls from 'widget/redux/client/actions/video-urls/fetch';
import reportStats from 'widget/redux/server/actions/stats/report';

import STATS_EVENTS from 'shared/constants/stats/events';


const VIDI_ERRORS = {
  SRC_LOAD_ERROR: 'SRC_LOAD_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};


/*
 * PlayerWrapper does these while wrapping VODPlayer:
 * - sends BI
 * - logs statistics
 * - checks memberOnly setting
 */
@connect((state, props) => ({
  videoUrls: getVideoUrls(state, props),
  appSettings: getAllSettings(state),
  locale: state.locale,
  translations: state.translations,
  newPlayerEnabled: isNewPlayerEnabled(state),
  currentSiteUser: getCurrentSiteUser(state),
}), {
  reportStats,
  fetchVideoUrls,
})
@VODBiEvents()
export default class PlayerWithStatAndBI extends React.Component {

  static propTypes = {
    width: React.PropTypes.number,
    height: React.PropTypes.number,
    videoUrls: React.PropTypes.object,
    videoItem: React.PropTypes.object,
    //remove after new player
    channelData: React.PropTypes.object,
    size: React.PropTypes.object,
    simple: React.PropTypes.bool,
    mobileMode: React.PropTypes.bool,
    overlay: React.PropTypes.any,
    onFullScreenChanged: React.PropTypes.func,
    openPreviewPrompt: React.PropTypes.func,
    onPlayStart: React.PropTypes.func,
    onEnded: React.PropTypes.func,
    reportStats: React.PropTypes.func,
    fetchVideoUrls: React.PropTypes.func,
    biEvent: React.PropTypes.func,
    appSettings: React.PropTypes.object,
    locale: React.PropTypes.string,
    translations: React.PropTypes.object,
    canShowChannelCover: React.PropTypes.bool,
    mainOrThumbnailLocation: React.PropTypes.string,
    muted: React.PropTypes.bool,
    loop: React.PropTypes.bool,
    showInitialOverlay: React.PropTypes.bool,
    currentSiteUser: React.PropTypes.object,
    memberOnlyOverlay: React.PropTypes.node,
    onMemberSignUp: React.PropTypes.func,
    currentTime: React.PropTypes.number,
    newPlayerEnabled: React.PropTypes.bool,
  };

  static defaultProps = {
    openPreviewPrompt: _.noop,
    onPlayStart: _.noop,
    onEnded: _.noop,
    onMemberSignUp: _.noop,
    mainOrThumbnailLocation: 'main',
    simple: false,
    mobileMode: false,
  };

  componentDidMount() {
    this.checkVideoUrls(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.checkVideoUrls(nextProps);
  }

  reloadCounts = {};

  checkVideoUrls({ fetchVideoUrls, videoItem, videoUrls }) {
    if (!videoUrls && !isExternalVideo(videoItem)) {
      if (!videoItem.memberOnly || this.isUserLogged) {
        fetchVideoUrls(videoItem);
      }
    }
  }

  get isUserLogged() {
    const { currentSiteUser } = this.props;

    return Boolean(currentSiteUser);
  }

  @autobind
  reloadVideoUrls(error) {
    if (!error) {
      return;
    }

    const { fetchVideoUrls, videoItem } = this.props;
    const { id } = videoItem;

    this.reloadCounts[id] = this.reloadCounts[id] || 0;
    this.reloadCounts[id] += 1;

    if (this.reloadCounts[id] > 1) {
      return;
    }

    if (error === VIDI_ERRORS.SRC_LOAD_ERROR) {
      fetchVideoUrls(videoItem, true);
    }
  }

  @autobind
  onPlayStart() {
    this.props.onPlayStart();
    this.sendVidStartPlayBI();
  }

  @autobind
  onEnded() {
    this.sendVidPlayedBI();

    const { onEnded, reportStats, channelData, videoItem, currentSiteUser } = this.props;
    onEnded();

    let event;
    if (canAccessFullVideo(channelData, videoItem, currentSiteUser)) {
      event = STATS_EVENTS.VIDEO_PLAY_COMPLETE;
    } else {
      event = STATS_EVENTS.VIDEO_PLAY_TRAILER;
    }

    reportStats(event, {
      channel: channelData,
      videoItem,
    });
  }

  sendVidStartPlayBI() {
    const { mainOrThumbnailLocation: button } = this.props;
    this.sendBI('widget.vid.start.play', { button });
  }

  sendVidPlayedBI() {
    const { mainOrThumbnailLocation: button } = this.props;
    this.sendBI('widget.vid.played', { button });
  }

  @autobind
  sendBIForShowActions(actionsList) {
    if (_.includes(actionsList, OVERLAY_ACTION_TYPES.BUY_RENT)) {
      this.sendBuyBI('widget.vid.buy.displayed');
    }
    if (_.includes(actionsList, OVERLAY_ACTION_TYPES.INFO)) {
      this.sendBI('widget.vid.info.displayed');
    }
    if (_.includes(actionsList, OVERLAY_ACTION_TYPES.SHARE)) {
      this.sendBI('widget.vid.share.displayed');
    }
  }

  @autobind
  sendBIForActionClicked(actionName, params) {
    if (actionName === OVERLAY_ACTION_TYPES.INFO) {
      this.sendBI('widget.vid.info.clicked');
    } else if (actionName === OVERLAY_ACTION_TYPES.BUY_RENT) {
      this.sendBuyBI('widget.vid.buy.clicked');
    } else if (actionName === OVERLAY_ACTION_TYPES.SHARE) {
      const { reportStats, channelData, videoItem } = this.props;

      reportStats(STATS_EVENTS.VIDEO_SHARE, {
        channel: channelData,
        videoItem,
        network: {
          name: params.networkName,
        },
      });

      this.sendBI('widget.vid.share.clicked', {
        button: params.networkName,
      });
    }
  }

  sendBI(name, params) {
    const { channelData, videoItem, biEvent } = this.props;
    const { id, videoSource } = videoItem;

    biEvent(name, _.assign({}, params, {
      channelID: channelData.id,
      videoID: id,
      videoType: videoSource,
    }));
  }

  sendBuyBI(name) {
    const { videoItem, channelData, mainOrThumbnailLocation: button } = this.props;

    const buyType = getBuyButtonDisplayType(channelData, videoItem);

    this.sendBI(name, {
      buyType,
      button,
    });
  }

  @autobind
  sendBIForFullScreenClicked(isFullScreen) {
    if (!isFullScreen) {
      this.sendBI('widget.vid.fullScreen.clicked');
    }
  }

  render() {
    const {
      size,
      translations,
      channelData,
      videoItem,
      memberOnlyOverlay,
      canShowChannelCover,
      width,
      height,
      appSettings,
      onMemberSignUp,
      newPlayerEnabled,
    } = this.props;

    const props = _.omit(
      this.props,
      'newPlayerEnabled',
      'size',
      'reportStats',
    );
    const PlayerComponent = (newPlayerEnabled) ? NewPlayer : VODPlayer;
    if (!videoItem.memberOnly || this.isUserLogged) {
      return (
        <PlayerComponent {...props}
                         {...size}
                         isFetchDisabled
                         translations={translations}
                         onPlayStart={this.onPlayStart}
                         onEnded={this.onEnded}
                         onError={this.reloadVideoUrls}
                         onShowActions={this.sendBIForShowActions}
                         onActionClicked={this.sendBIForActionClicked}
                         onFullScreenClicked={this.sendBIForFullScreenClicked} />
      );
    }

    return React.cloneElement(memberOnlyOverlay, {
      showChannelCover: canShowChannelCover,
      videoItem,
      width,
      height,
      appSettings,
      channelData,
      onPlayClick: onMemberSignUp,
    });
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/players/player-wrapper.js